/**
 * TripItemsTable — Reusable items table for either purchase list or delivery list.
 * Used in TripForm for direct trips (SUPPLIER→CUSTOMER) where two separate lists exist.
 */
import React from 'react';
import { Control, Controller, UseFormRegister, FieldArrayWithId } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { SearchableCombobox, ComboboxOption } from '../UI/SearchableCombobox';
import CurrencyInput from '../UI/CurrencyInput';

// Minimal item row value shape for watched fields
export interface ItemRowValue {
  itemId?: number | null;
  itemName: string;
  volumeWeight: string;
  unit: string;
  purchasePrice: string;
  salePrice: string;
  unitPrice: string;
  isGiftFromSupplier?: boolean;
  isGiftToCustomer?: boolean;
}

export type PriceType = 'purchase' | 'sale';

interface TripItemsTableProps {
  // Table header info
  title: string;
  accentColor: 'orange' | 'blue';
  priceType: PriceType;

  // react-hook-form field array
  fields: FieldArrayWithId[];
  items: ItemRowValue[];  // watched values for live totals
  fieldArrayPath: 'purchaseItems' | 'deliveryItems';
  control: Control<any>;
  register: UseFormRegister<any>;

  // Callbacks
  onAppend: () => void;
  onRemove: (index: number) => void;
  onItemSelect: (index: number, itemId: number | string | null) => void;

  // Data
  itemOptions: ComboboxOption[];
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('vi-VN').format(value);

export const TripItemsTable: React.FC<TripItemsTableProps> = ({
  title,
  accentColor,
  priceType,
  fields,
  items,
  fieldArrayPath,
  control,
  register,
  onAppend,
  onRemove,
  onItemSelect,
  itemOptions,
}) => {
  const { t } = useTranslation();

  const priceField = priceType === 'purchase' ? 'purchasePrice' : 'salePrice';
  const giftField = priceType === 'purchase' ? 'isGiftFromSupplier' : 'isGiftToCustomer';
  const headerColorClass = accentColor === 'orange' ? 'text-orange-500' : 'text-blue-500';
  const totalColorClass = accentColor === 'orange' ? 'text-orange-600' : 'text-blue-600';
  const inputColorClass = accentColor === 'orange' ? 'input-field text-orange-700' : 'input-field text-blue-700';

  // Calculate total — gift items contribute 0
  const totalValue = items.reduce((sum, item) => {
    const isGift = priceType === 'purchase' ? item.isGiftFromSupplier : item.isGiftToCustomer;
    if (isGift) return sum;
    const qty = parseFloat(item.volumeWeight) || 0;
    const price = parseFloat(priceType === 'purchase' ? item.purchasePrice : item.salePrice) || 0;
    return sum + qty * price;
  }, 0);

  return (
    <div className="border rounded-lg p-4">
      {/* Table header */}
      <div className="flex justify-between items-center mb-3">
        <h3 className={`font-semibold ${totalColorClass}`}>{title}</h3>
        <button
          type="button"
          onClick={onAppend}
          className="btn-secondary flex items-center text-sm"
        >
          <Plus className="w-4 h-4 mr-1" />
          {t('trips.addItem')}
        </button>
      </div>

      {/* Column headers */}
      <div className="grid grid-cols-12 gap-2 mb-1">
        <div className="col-span-4 text-xs font-medium text-gray-500">{t('trips.itemName')}</div>
        <div className="col-span-2 text-xs font-medium text-gray-500">{t('trips.volumeWeight')}</div>
        <div className="col-span-1 text-xs font-medium text-gray-500">{t('common.unit')}</div>
        <div className={`col-span-2 text-xs font-medium ${headerColorClass}`}>
          {priceType === 'purchase' ? t('trips.purchasePrice') : t('trips.salePrice')}
        </div>
        <div className="col-span-2 text-xs font-medium text-gray-500 text-right">{t('trips.lineTotal')}</div>
        <div className="col-span-1" />
      </div>

      {/* Rows */}
      <div className="space-y-2">
        {fields.map((field, index) => {
          const isGift = priceType === 'purchase'
            ? items[index]?.isGiftFromSupplier
            : items[index]?.isGiftToCustomer;

          return (
            <div key={field.id} className="grid grid-cols-12 gap-2 items-start">
              {/* Item name */}
              <div className="col-span-4">
                <Controller
                  name={`${fieldArrayPath}.${index}.itemId`}
                  control={control}
                  render={({ field: itemField }) => (
                    <SearchableCombobox
                      options={itemOptions}
                      value={itemField.value ?? null}
                      onChange={(val) => onItemSelect(index, val)}
                      placeholder={t('trips.itemName')}
                    />
                  )}
                />
                <input type="hidden" {...register(`${fieldArrayPath}.${index}.itemName`)} />
              </div>

              {/* Volume/Weight */}
              <div className="col-span-2">
                <input
                  type="number"
                  step="0.01"
                  placeholder="0"
                  {...register(`${fieldArrayPath}.${index}.volumeWeight`)}
                  className="input-field"
                />
              </div>

              {/* Unit */}
              <div className="col-span-1">
                <select {...register(`${fieldArrayPath}.${index}.unit`)} className="input-field">
                  <option value="M3">{t('items.units.M3')}</option>
                  <option value="TON">{t('items.units.TON')}</option>
                  <option value="KG">{t('items.units.KG')}</option>
                  <option value="PIECE">{t('items.units.PIECE')}</option>
                </select>
              </div>

              {/* Price input + gift toggle */}
              <div className="col-span-2">
                {isGift ? (
                  // Gift item: show "Được tặng" / "Tặng KH" label instead of price input
                  <div className={`text-xs font-medium px-2 py-1.5 rounded border ${
                    priceType === 'purchase'
                      ? 'text-green-700 bg-green-50 border-green-200'
                      : 'text-purple-700 bg-purple-50 border-purple-200'
                  }`}>
                    {t('trips.giftFree')}
                  </div>
                ) : (
                  <Controller
                    name={`${fieldArrayPath}.${index}.${priceField}`}
                    control={control}
                    render={({ field: priceFieldCtrl }) => (
                      <CurrencyInput
                        value={priceFieldCtrl.value}
                        onChange={priceFieldCtrl.onChange}
                        placeholder="0"
                        className={inputColorClass}
                      />
                    )}
                  />
                )}

                {/* Gift toggle checkbox */}
                <label className={`flex items-center gap-1 mt-1 text-xs cursor-pointer ${
                  priceType === 'purchase' ? 'text-green-600' : 'text-purple-600'
                }`}>
                  <input
                    type="checkbox"
                    {...register(`${fieldArrayPath}.${index}.${giftField}`)}
                    className="rounded"
                  />
                  {priceType === 'purchase' ? t('trips.giftFromSupplier') : t('trips.giftToCustomer')}
                </label>

                {/* Hidden fields for the other price */}
                <input type="hidden" {...register(`${fieldArrayPath}.${index}.unitPrice`)} />
                {priceType === 'purchase'
                  ? <input type="hidden" {...register(`${fieldArrayPath}.${index}.salePrice`)} />
                  : <input type="hidden" {...register(`${fieldArrayPath}.${index}.purchasePrice`)} />
                }
              </div>

              {/* Line total */}
              <div className={`col-span-2 text-right text-xs font-medium pt-1 ${isGift ? 'text-gray-400' : totalColorClass}`}>
                {isGift ? t('trips.giftFree') : formatCurrency(
                  (parseFloat(items[index]?.volumeWeight) || 0) *
                  (parseFloat(priceType === 'purchase' ? items[index]?.purchasePrice : items[index]?.salePrice) || 0)
                )}
              </div>

              {/* Delete */}
              <div className="col-span-1 flex justify-center pt-1">
                <button
                  type="button"
                  onClick={() => onRemove(index)}
                  className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                  disabled={fields.length === 1}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Total row */}
      <div className="mt-3 pt-3 border-t flex justify-end">
        <span className={`text-sm font-semibold ${totalColorClass}`}>
          {priceType === 'purchase' ? t('trips.totalPurchaseValue') : t('trips.totalSaleValue')}:{' '}
          {formatCurrency(totalValue)} đ
        </span>
      </div>
    </div>
  );
};
